import { HoverOnlyTooltipComponent } from '@/components/Tooltip';
import { getStateOptions } from '@/helpers/getStateOptions';
import {
  useDeleteOrganizationUnderwritingSettingMutation,
  useGetEffectiveOrganizationUnderwritingSettingsQuery,
  useGetOrganizationUnderwritingSettingsQuery,
  usePutOrganizationUnderwritingSettingsMutation,
  OrganizationUnderwritingSettingsStateOverride,
} from '@/services/organizations';
import { OrganizationNode } from '@/types/Organizations';
import { Box, Button, Icon, useOpenClose } from '@palmetto/palmetto-components';
import { ReactNode, useCallback, useMemo, useState } from 'react';

import { OrganizationUnderwritingSettingsV2, ProgramType } from 'types';

import { OrganizationSettingsCard } from '../shared/OrganizationSettingsCard';

import {
  CREDIT_OPTIMIZATION_TOOLTIP,
  MULTI_APPLICANT_MIN_FICO_TOOLTIP,
  noop,
  tooltipContainerStyle,
  tooltipContentStyle,
  tooltipTriggerStyle,
} from './constants';
import { UnderwritingSettingsModal } from './UnderwritingSettingsModal';

interface UnderwritingSettingsProps {
  organization: OrganizationNode;
  programType: ProgramType;
  isFicoAdmin: boolean;
}

const FIELD_LABELS: Record<keyof OrganizationUnderwritingSettingsV2, string> = {
  minFicoScore: 'Min FICO Score',
  ficoScoreErrorThreshold: 'FICO Error Threshold',
  minFicoToRerun: 'Min FICO to Rerun',
  creditExpiryTerm: 'Credit Expiry Term (days)',
  yearsSinceLastBankruptcy: 'Years Since Bankruptcy',
  maxAppsPerDay: 'Max Apps / Day',
  autoQualify: 'Auto Qualify',
  multipleApplicantMinimumFico: 'Min FICO Score (Co-Applicant)',
  creditOptimizationMin: 'Credit Optimization Min',
  creditOptimizationMax: 'Credit Optimization Max',
  state: 'State',
  setBy: 'Set By',
};

const FIELD_TOOLTIPS: Partial<Record<keyof OrganizationUnderwritingSettingsV2, string>> = {
  multipleApplicantMinimumFico: MULTI_APPLICANT_MIN_FICO_TOOLTIP,
  creditOptimizationMin: CREDIT_OPTIMIZATION_TOOLTIP,
  creditOptimizationMax: CREDIT_OPTIMIZATION_TOOLTIP,
};

const DISPLAY_FIELDS: (keyof OrganizationUnderwritingSettingsV2)[] = [
  'minFicoScore',
  'multipleApplicantMinimumFico',
  'ficoScoreErrorThreshold',
  'minFicoToRerun',
  'creditOptimizationMin',
  'creditOptimizationMax',
  'yearsSinceLastBankruptcy',
  'maxAppsPerDay',
  'autoQualify',
];

function formatValue(
  key: keyof OrganizationUnderwritingSettingsV2,
  value: OrganizationUnderwritingSettingsV2[typeof key],
): string {
  if (value === undefined || value === null) return '—';
  if (key === 'autoQualify') return value ? 'Yes' : 'No';
  return String(value);
}

interface SettingFieldProps {
  fieldKey: keyof OrganizationUnderwritingSettingsV2;
  orgVal: OrganizationUnderwritingSettingsV2[keyof OrganizationUnderwritingSettingsV2];
  refVal: OrganizationUnderwritingSettingsV2[keyof OrganizationUnderwritingSettingsV2];
  effectiveVal: OrganizationUnderwritingSettingsV2[keyof OrganizationUnderwritingSettingsV2] | undefined;
  hasSettings: boolean;
  isFicoAdmin: boolean;
}

function SettingField({ fieldKey, orgVal, refVal, effectiveVal, hasSettings, isFicoAdmin }: SettingFieldProps) {
  const displayVal = hasSettings ? orgVal : effectiveVal;
  return (
    <Box padding="md" borderWidth="xs" borderColor="separator">
      <Box direction="row" alignItems="center" childGap="2xs">
        <Box fontSize="xs" fontWeight="bold" color="grey-500" style={{ textTransform: 'uppercase' }}>
          {FIELD_LABELS[fieldKey]}
        </Box>
        {FIELD_TOOLTIPS[fieldKey] && (
          <HoverOnlyTooltipComponent
            content={FIELD_TOOLTIPS[fieldKey]}
            onClose={noop}
            displayedContainerStyle={tooltipTriggerStyle}
            contentStyle={tooltipContentStyle}
            tooltipContainerStyle={tooltipContainerStyle}
          >
            <Icon name="c-info" size="sm" color="grey-500" />
          </HoverOnlyTooltipComponent>
        )}
      </Box>
      <Box fontSize="lg" fontWeight="medium" margin="xs 0 0 0">
        {formatValue(fieldKey, displayVal)}
      </Box>
      {isFicoAdmin && refVal !== undefined && (
        <Box fontSize="xs" color="grey-500" margin="2xs 0 0 0">
          Sabal: {formatValue(fieldKey, refVal)}
        </Box>
      )}
    </Box>
  );
}

interface StateOverrideRowProps {
  override: OrganizationUnderwritingSettingsStateOverride;
  stateName: string;
  isInherited: boolean;
  isFicoAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function StateOverrideRow({
  override,
  stateName,
  isInherited,
  isFicoAdmin,
  onEdit,
  onDelete,
}: StateOverrideRowProps) {
  const v = override.settings.value;
  return (
    <Box
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      padding="sm md"
      borderWidth="xs"
      borderColor="separator"
      background="primary"
      style={{ opacity: isInherited ? 0.65 : 1 }}
    >
      <Box direction="row" alignItems="center" childGap="lg" flex="auto">
        <Box minWidth="3xl">
          <Box fontWeight="bold">
            {stateName} ({override.settings.value?.state})
          </Box>
          {isInherited && (
            <Box fontSize="xs" color="warning-500" fontStyle="italic">
              Inherited from {override.sourceAlias}
            </Box>
          )}
        </Box>
        <Box fontSize="sm" color="grey-600" direction="row" childGap="lg" flex="auto">
          {DISPLAY_FIELDS.map((key) => (
            <Box key={key}>
              <Box fontSize="xs" color="grey-500">
                {FIELD_LABELS[key]}
              </Box>
              <Box>{formatValue(key, v?.[key])}</Box>
            </Box>
          ))}
        </Box>
      </Box>
      {isFicoAdmin && (
        <Box direction="row" childGap="sm">
          <Button size="sm" variant="secondary" tone="neutral" onClick={onEdit}>
            {isInherited ? 'Override' : 'Edit'}
          </Button>
          {!isInherited && (
            <Button size="sm" variant="secondary" tone="danger" onClick={onDelete}>
              Delete
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

export function UnderwritingSettings({ organization, programType, isFicoAdmin }: UnderwritingSettingsProps) {
  const { isOpen, handleOpen, handleClose } = useOpenClose();
  const {
    isOpen: isStateOverrideOpen,
    handleOpen: handleOpenStateOverride,
    handleClose: handleCloseStateOverride,
  } = useOpenClose();
  const [editingStateOverride, setEditingStateOverride] =
    useState<OrganizationUnderwritingSettingsStateOverride | null>(null);

  const stateNameMap = useMemo(() => {
    const map = new Map<string, string>();
    getStateOptions().forEach(({ label, value }) => map.set(value, label));
    return map;
  }, []);

  const { data, isFetching } = useGetOrganizationUnderwritingSettingsQuery({
    alias: organization.alias,
    programType,
  });
  const settings = data?.settings ?? null;
  const stateOverrides = data?.stateOverrides ?? [];

  const { data: sabalEffective } = useGetEffectiveOrganizationUnderwritingSettingsQuery(
    { alias: 'sabal', programType },
    { skip: !isFicoAdmin },
  );
  const sabalReference = sabalEffective?.settings.value ?? undefined;

  const [putSettings] = usePutOrganizationUnderwritingSettingsMutation();
  const [deleteSetting] = useDeleteOrganizationUnderwritingSettingMutation();

  const hasSettings = !!settings;

  const { data: effectiveSettings } = useGetEffectiveOrganizationUnderwritingSettingsQuery(
    { alias: organization.alias, programType },
    { skip: hasSettings },
  );

  const inheritLabel = effectiveSettings?.sourceAlias
    ? `Inheriting from ${effectiveSettings.sourceAlias}`
    : 'Inheriting from Sabal';

  const handleSubmit = useCallback(
    async (value: OrganizationUnderwritingSettingsV2, effectiveDate: string) => {
      await putSettings({ alias: organization.alias, programType, value, effectiveDate }).unwrap();
      handleClose();
    },
    [putSettings, organization.alias, programType, handleClose],
  );

  const handleEditStateOverride = useCallback(
    (override: OrganizationUnderwritingSettingsStateOverride) => {
      setEditingStateOverride(override);
      handleOpenStateOverride();
    },
    [handleOpenStateOverride],
  );

  const handleAddStateOverride = useCallback(() => {
    setEditingStateOverride(null);
    handleOpenStateOverride();
  }, [handleOpenStateOverride]);

  const handleCloseStateOverrideModal = useCallback(() => {
    setEditingStateOverride(null);
    handleCloseStateOverride();
  }, [handleCloseStateOverride]);

  const handleSubmitStateOverride = useCallback(
    async (value: OrganizationUnderwritingSettingsV2, effectiveDate: string) => {
      await putSettings({ alias: organization.alias, programType, value, effectiveDate }).unwrap();
      handleCloseStateOverrideModal();
    },
    [putSettings, organization.alias, programType, handleCloseStateOverrideModal],
  );

  const handleDeleteSettings = useCallback(async () => {
    if (!settings?._id) return;
    if (
      !window.confirm(
        'Delete these underwriting settings? The organization will revert to inheriting from its parent.',
      )
    )
      return;
    await deleteSetting({ alias: organization.alias, programType, settingsId: settings._id }).unwrap();
  }, [deleteSetting, organization.alias, programType, settings]);

  const handleDeleteStateOverride = useCallback(
    async (override: OrganizationUnderwritingSettingsStateOverride) => {
      if (
        !window.confirm(`Delete the ${override.settings.value?.state} state override? It will revert to inheriting.`)
      )
        return;
      await deleteSetting({ alias: organization.alias, programType, settingsId: override.settings._id }).unwrap();
    },
    [deleteSetting, organization.alias, programType],
  );

  const renderEditButtonContent = useCallback(
    (button: ReactNode): ReactNode => {
      if (!isFicoAdmin) return null;
      return (
        <Box direction="row" childGap="sm" alignItems="center">
          {hasSettings && (
            <Button size="sm" variant="secondary" tone="danger" onClick={handleDeleteSettings}>
              Delete
            </Button>
          )}
          {button}
        </Box>
      );
    },
    [isFicoAdmin, hasSettings, handleDeleteSettings],
  );

  const cardSubtitle = useMemo(
    () =>
      !hasSettings ? (
        <Box fontSize="sm" color="warning-500" fontStyle="italic">
          {inheritLabel}
        </Box>
      ) : undefined,
    [hasSettings, inheritLabel],
  );

  const editingIsInherited =
    editingStateOverride !== null && editingStateOverride.sourceAlias !== organization.alias;

  return (
    <>
      {isFicoAdmin && isOpen && (
        <UnderwritingSettingsModal
          isOpen={isOpen}
          onClose={handleClose}
          onSubmit={handleSubmit}
          currentSettings={settings?.value ?? undefined}
          sabalReference={sabalReference}
          programType={programType}
          isCreate={!hasSettings}
        />
      )}

      {isFicoAdmin && isStateOverrideOpen && (
        <UnderwritingSettingsModal
          isOpen={isStateOverrideOpen}
          onClose={handleCloseStateOverrideModal}
          onSubmit={handleSubmitStateOverride}
          currentSettings={
            editingStateOverride ? editingStateOverride.settings.value : (settings?.value ?? undefined)
          }
          sabalReference={sabalReference}
          programType={programType}
          isCreate={!editingStateOverride || editingIsInherited}
          isStateOverride
          stateCode={editingStateOverride?.settings.value?.state}
        />
      )}

      <OrganizationSettingsCard
        cardTitle="Underwriting Settings"
        subtitle={cardSubtitle}
        organization={organization}
        isLoading={isFetching}
        onClick={isFicoAdmin ? handleOpen : undefined}
        editButtonLabel={hasSettings ? 'Edit' : '+ Create'}
        renderEditButton={renderEditButtonContent}
      >
        <Box
          padding="0 lg lg lg"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', opacity: hasSettings ? 1 : 0.45 }}
        >
          {DISPLAY_FIELDS.map((key) => (
            <SettingField
              key={key}
              fieldKey={key}
              orgVal={settings?.value?.[key]}
              refVal={sabalReference?.[key]}
              effectiveVal={effectiveSettings?.settings.value?.[key]}
              hasSettings={hasSettings}
              isFicoAdmin={isFicoAdmin}
            />
          ))}
        </Box>
      </OrganizationSettingsCard>

      <Box margin="lg 0 0 0">
        <Box direction="row" justifyContent="space-between" alignItems="center" margin="0 0 sm 0">
          <Box fontWeight="bold" fontSize="md">
            State Overrides
          </Box>
          {isFicoAdmin && (
            <Button size="sm" variant="secondary" tone="neutral" onClick={handleAddStateOverride}>
              + Add State Override
            </Button>
          )}
        </Box>
        {stateOverrides.length === 0 ? (
          <Box fontSize="sm" color="grey-500" fontStyle="italic">
            No state-specific overrides configured.
          </Box>
        ) : (
          <Box style={{ display: 'grid', gap: '8px' }}>
            {stateOverrides.map((override) => (
              <StateOverrideRow
                key={override.settings._id}
                override={override}
                stateName={
                  stateNameMap.get(override.settings.value?.state ?? '') ?? override.settings.value?.state ?? ''
                }
                isInherited={override.sourceAlias !== organization.alias}
                isFicoAdmin={isFicoAdmin}
                onEdit={() => handleEditStateOverride(override)}
                onDelete={() => handleDeleteStateOverride(override)}
              />
            ))}
          </Box>
        )}
      </Box>
    </>
  );
}
